import React, { useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Button, IconButton } from 'src/components/design-system';
import { Select } from 'src/components/design-system/forms';
import { NotFound } from 'src/components/common';
import classNames from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faTrashAlt } from '@fortawesome/pro-regular-svg-icons';
import { notification } from 'antd';
import styles from './team-manager.module.scss';
import { useSelector } from 'react-redux';
import { getLang } from 'shared/store/selectors/lang.selector';
import { ShareBoxComponent } from '../share';
import { formatFullName } from 'shared/utils';
import { ROUTES } from 'shared/constants';
import {
  Table,
  cellActionKeyboardNavigate,
  textWithFallback,
} from 'src/components/design-system/table';

const TeamManager = ({
  existingUsers = [],
  availableUsers = [],
  onAddUsers = () => {},
  onRemoveUser = () => {},
  skipRemoveUserToast = false,
  canEdit = false,
  notFoundText = '',
  disabledIds = [],
  rootClassName = '',
  /** Accessible name for the add-members combobox — it has no visible label of its own. */
  addUsersLabel = '',
}) => {
  const userLang = useSelector(getLang('CONTACT_SCREEN'));
  const roleLang = useSelector(getLang('PROFILE_OPTIONS'));
  const departmentLang = useSelector(getLang('TAGS'));
  const lang = useSelector(getLang('TEAM_MANAGER'));
  const [selectedUsers, setSelectedUsers] = React.useState([]);
  const [loadingAdd, setLoadingAdd] = React.useState(false);
  const [removingId, setRemovingId] = React.useState(null);
  const [api, contextHolder] = notification.useNotification({ placement: 'bottomRight' });

  const handleAddUsers = async () => {
    setLoadingAdd(true);
    try {
      const res = await onAddUsers(selectedUsers);
      if (res?.error) {
        api.error({ title: res.error || lang.ADD_FAILED });
      } else {
        api.success({ title: lang.ADD_SUCCESSFUL });
      }
      setSelectedUsers([]);
    } catch (error) {
      api.error({ title: error.message || lang.ADD_FAILED });
    } finally {
      setLoadingAdd(false);
    }
  };

  const handleRemoveUser = useCallback(
    async userId => {
      if (removingId === userId) return;
      setRemovingId(userId);
      try {
        await onRemoveUser(userId);
        !skipRemoveUserToast && api.success({ title: lang.REMOVE_SUCCESSFUL });
      } catch (error) {
        api.error({ title: error.message || lang.REMOVE_FAILED });
      } finally {
        setRemovingId(null);
      }
    },
    [
      removingId,
      onRemoveUser,
      skipRemoveUserToast,
      api,
      lang.REMOVE_SUCCESSFUL,
      lang.REMOVE_FAILED,
    ],
  );

  // Prepare data source with computed fields
  const dataSource = useMemo(() => {
    return existingUsers
      .filter(user => !!user)
      .map(user => {
        const { role = '', department = '' } = user.professional || {};
        const professionalLabel = [roleLang[role], departmentLang[department]]
          .filter(v => v !== undefined)
          .join(', ');

        return {
          ...user,
          key: user.id,
          professionalLabel,
          shareURL: ROUTES.CONTACTS_PREVIEW.replace(':id', user.id),
          isDisabled: disabledIds.includes(user.id),
        };
      });
  }, [existingUsers, roleLang, departmentLang, disabledIds]);

  // Define column definitions for AgGrid
  const columnDefs = useMemo(() => {
    const baseColumns = [
      {
        headerName: userLang.NAME,
        field: 'name',
        minWidth: 250,
        flex: 1,
        autoHeight: true,
        cellRenderer: params => (
          <div className={styles.nameCell}>
            <Link className={classNames([styles.nameValue, styles.link])} to={params.data.shareURL}>
              {formatFullName({ firstName: params.data.firstName, lastName: params.data.lastName })}
            </Link>
            {params.data.professionalLabel && (
              <span className={styles.professionalValue}>{params.data.professionalLabel}</span>
            )}
          </div>
        ),
        suppressKeyboardEvent: cellActionKeyboardNavigate,
      },
      {
        flex: 1,
        headerName: userLang.PHONE,
        field: 'phone',
        valueFormatter: textWithFallback,
      },
      {
        flex: 1,
        headerName: userLang.PAGER,
        field: 'pager',
        valueFormatter: textWithFallback,
      },
      {
        flex: 1,
        headerName: userLang.CISCO,
        field: 'cisco',
        valueFormatter: textWithFallback,
      },
      {
        flex: 1,
        headerName: userLang.EMAIL,
        field: 'email',
        cellRenderer: params =>
          params.value ? (
            <a
              href={`mailto:${params.value}`}
              className={classNames(styles.link)}
              title={params.value}>
              {params.value}
            </a>
          ) : (
            '-'
          ),
        suppressKeyboardEvent: cellActionKeyboardNavigate,
      },
      {
        flex: 1,
        headerName: userLang.ACTIONS,
        field: 'actions',
        width: 120,
        cellRenderer: params => (
          <div className={styles.actionsCell}>
            <ShareBoxComponent
              iconClassName={classNames([styles.icon, styles.shareIcon])}
              path={params.data.shareURL}
            />
            {canEdit && (
              <IconButton
                label={params.data.isDisabled ? lang.CANNOT_REMOVE_MANAGER : lang.REMOVE}
                disabled={params.data.isDisabled}
                onClick={e => {
                  e.stopPropagation();
                  handleRemoveUser(params.data.id);
                }}
                className={styles.buttonIcon}>
                <FontAwesomeIcon
                  icon={removingId === params.data.id ? faSpinner : faTrashAlt}
                  spin={removingId === params.data.id}
                  className={classNames([
                    styles.icon,
                    styles.removeIcon,
                    { [styles.disabled]: params.data.isDisabled },
                  ])}
                />
              </IconButton>
            )}
          </div>
        ),
        suppressKeyboardEvent: cellActionKeyboardNavigate,
        sortable: false,
      },
    ];

    // Remove actions column if canEdit is false
    if (!canEdit) {
      return baseColumns.slice(0, -1);
    }

    return baseColumns;
  }, [userLang, lang, canEdit, removingId, handleRemoveUser]);

  return (
    <div className={classNames(styles.root, rootClassName)}>
      {contextHolder}
      {canEdit && (
        <div className={styles.addNew}>
          <Select
            mode="multiple"
            size="large"
            aria-label={addUsersLabel || undefined}
            onChange={(f, value) => setSelectedUsers(value)}
            options={availableUsers}
            value={selectedUsers}
          />
          <Button
            type="primary"
            onClick={handleAddUsers}
            size="large"
            disabled={loadingAdd || !selectedUsers.length}>
            {userLang.ADD}
          </Button>
        </div>
      )}
      {dataSource.length ? (
        <Table
          columnDefs={columnDefs}
          rowData={dataSource}
          getRowId={params => params.data.id}
          domLayout="autoHeight"
          paginationPageSize={10}
          pagination={dataSource.length > 10}
        />
      ) : (
        <NotFound text={notFoundText} />
      )}
    </div>
  );
};

export { TeamManager };
